import { linear, LineSymbolizer, PaintRule, PolygonSymbolizer } from 'protomaps-leaflet'

const VEGETATION_FILL = '#c5e8c5'
const WETLAND_FILL = '#b3d9d9'
const ROAD_CASING_COLOR = '#707070'
const TUNNEL_DASH = [4, 3]

const ADMIN_LEVEL_STYLES: Record<number, { dash?: number[]; width: number; minzoom?: number }> = {
  1: { dash: [15, 5, 5, 5], width: 1.4 },
  2: { dash: [15, 15], width: 1.4 },
  3: { width: 1, minzoom: 2 },
  4: { dash: [15, 5, 5, 5], width: 1, minzoom: 2 },
  5: { dash: [15, 15], width: 0.8, minzoom: 5 },
  6: { dash: [5, 5], width: 0.8, minzoom: 6 },
  7: { width: 0.5, minzoom: 8 },
  8: { dash: [15, 5, 5, 5], width: 0.5, minzoom: 9 },
  9: { dash: [15, 15], width: 0.5, minzoom: 9 },
  10: { dash: [5, 20], width: 0.5, minzoom: 10 },
  11: { dash: [1, 5], width: 0.5, minzoom: 11 },
}

const ROAD_CLASS_STYLE: {
  classes: string[]
  core: string
  minzoom?: number
  casingWidth: (z: number) => number
  coreWidth: (z: number) => number
}[] = [
  {
    classes: ['motorway'],
    core: '#809bc0',
    casingWidth: linear([
      [3, 0.6],
      [14, 2.4],
    ]),
    coreWidth: linear([
      [3, 0.4],
      [14, 1.8],
    ]),
  },
  {
    classes: ['trunk'],
    core: '#fff2af',
    casingWidth: linear([
      [4, 0.5],
      [14, 2.2],
    ]),
    coreWidth: linear([
      [4, 0.3],
      [14, 1.6],
    ]),
  },
  {
    classes: ['primary'],
    core: '#ffffff',
    minzoom: 5,
    casingWidth: linear([
      [5, 0.4],
      [14, 2.0],
    ]),
    coreWidth: linear([
      [5, 0.3],
      [14, 1.5],
    ]),
  },
  {
    classes: ['secondary'],
    core: '#ffffff',
    minzoom: 7,
    casingWidth: linear([
      [7, 0.4],
      [14, 1.9],
    ]),
    coreWidth: linear([
      [7, 0.3],
      [14, 1.4],
    ]),
  },
  {
    classes: ['tertiary'],
    core: '#ffffff',
    minzoom: 9,
    casingWidth: linear([
      [9, 0.4],
      [14, 1.7],
    ]),
    coreWidth: linear([
      [9, 0.3],
      [14, 1.3],
    ]),
  },
  {
    classes: ['minor', 'service'],
    core: '#ffffff',
    minzoom: 11,
    casingWidth: linear([
      [11, 0.4],
      [14, 1.4],
    ]),
    coreWidth: linear([
      [11, 0.3],
      [14, 1.0],
    ]),
  },
  {
    classes: ['busway', 'raceway', 'transit'],
    core: '#ffffff',
    minzoom: 11,
    casingWidth: linear([
      [11, 0.4],
      [14, 1.3],
    ]),
    coreWidth: linear([
      [11, 0.3],
      [14, 0.9],
    ]),
  },
]

const PATH_SUBCLASS_STYLE: { subclasses: string[]; core: string; width: number; dash?: number[] }[] = [
  { subclasses: ['cycleway'], core: '#d1fad1', width: 0.9 },
  { subclasses: ['footway', 'pedestrian', 'corridor'], core: '#eeeeee', width: 0.6 },
  { subclasses: ['steps'], core: '#eeeeee', width: 0.45, dash: [1.5, 1.5] },
]
const PATH_MINZOOM = 13
const TRACK_MINZOOM = 11
const RAIL_MINZOOM = 6

function roadClassOf(f: { props: { class?: unknown } }): string {
  return String(f.props.class ?? '')
}

function pathSubclassOf(f: { props: { subclass?: unknown } }): string {
  return String(f.props.subclass ?? '')
}

function isTunnel(f: { props: { brunnel?: unknown } }): boolean {
  return f.props.brunnel === 'tunnel'
}

export const PAINT_RULES: PaintRule[] = [
  {
    dataLayer: 'landcover',
    filter: (_z, f) => f.props.subclass !== 'wetland',
    symbolizer: new PolygonSymbolizer({ fill: VEGETATION_FILL }),
  },
  {
    dataLayer: 'landcover',
    filter: (_z, f) => f.props.subclass === 'wetland',
    symbolizer: new PolygonSymbolizer({ fill: WETLAND_FILL }),
  },
  {
    dataLayer: 'park',
    symbolizer: new PolygonSymbolizer({ fill: VEGETATION_FILL }),
  },
  {
    dataLayer: 'landuse',
    filter: (_z, f) => f.props.class === 'hospital',
    symbolizer: new PolygonSymbolizer({ fill: '#fde3e3' }),
  },
  {
    dataLayer: 'landuse',
    filter: (_z, f) => f.props.class === 'cemetery',
    symbolizer: new PolygonSymbolizer({ fill: '#c8dfc8' }),
  },
  {
    dataLayer: 'water',
    symbolizer: new PolygonSymbolizer({ fill: '#aadaff' }),
  },
  ...Object.entries(ADMIN_LEVEL_STYLES).map(
    ([level, style]): PaintRule => ({
      dataLayer: 'boundary',
      minzoom: style.minzoom,
      filter: (_z, f) => Number(f.props.admin_level) === Number(level),
      symbolizer: new LineSymbolizer({
        color: '#f9574b',
        width: style.width,
        dash: style.dash,
        dashColor: '#f9574b',
      }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, casingWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => isTunnel(f) && classes.includes(roadClassOf(f)),
      symbolizer: new LineSymbolizer({
        color: ROAD_CASING_COLOR,
        width: casingWidth,
        dash: TUNNEL_DASH,
        dashColor: ROAD_CASING_COLOR,
      }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, casingWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => !isTunnel(f) && classes.includes(roadClassOf(f)),
      symbolizer: new LineSymbolizer({ color: ROAD_CASING_COLOR, width: casingWidth }),
    })
  ),
  ...ROAD_CLASS_STYLE.map(
    ({ classes, core, coreWidth, minzoom }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom,
      filter: (_z, f) => !isTunnel(f) && classes.includes(roadClassOf(f)),
      symbolizer: new LineSymbolizer({ color: core, width: coreWidth }),
    })
  ),
  {
    dataLayer: 'transportation',
    minzoom: TRACK_MINZOOM,
    filter: (_z, f) => roadClassOf(f) === 'track',
    symbolizer: new LineSymbolizer({ color: '#d79331', width: 0.9, dash: [4, 2] }),
  },
  ...PATH_SUBCLASS_STYLE.map(
    ({ subclasses, core, width, dash }): PaintRule => ({
      dataLayer: 'transportation',
      minzoom: PATH_MINZOOM,
      filter: (_z, f) => roadClassOf(f) === 'path' && subclasses.includes(pathSubclassOf(f)),
      symbolizer: new LineSymbolizer({ color: core, width, dash }),
    })
  ),
  {
    dataLayer: 'transportation',
    minzoom: RAIL_MINZOOM,
    filter: (_z, f) => roadClassOf(f) === 'rail',
    symbolizer: new LineSymbolizer({ color: '#333333', width: 0.6 }),
  },
  {
    dataLayer: 'transportation',
    minzoom: RAIL_MINZOOM,
    filter: (_z, f) => roadClassOf(f) === 'rail',
    symbolizer: new LineSymbolizer({ color: '#e6e6e6', width: 0.4, dash: [3, 3] }),
  },
  {
    dataLayer: 'building',
    minzoom: 13,
    symbolizer: new PolygonSymbolizer({ fill: '#d9d0c9', stroke: '#c8bcae' }),
  },
]
